import { Grid, FormControl, Typography, Slider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { touchpointsMocks } from 'pages/calculator/MediaPlanning/components/Touchpoints/mocks';
import { YouTubeTouchpointStateKeys } from 'pages/calculator/MediaPlanning/components/Touchpoints/interfaces';
import convertDecimalToPercentValue from '../../../../Plan/Countries/utils/convertDecimalToPercentValue';
import usePlanStore from '../../../../../../../store/planStore';
import ErrorMessage from '../../../../common/ErrorMessage/ErrorMessage';
import HelperTooltip from '../../../../../../../shared/HelperTooltip/HelperTooltip';
import linksConfig from '../../../../../../../config/linking';
import MultiSelectAutocomplete from '../../../../../../../shared/components/MultiSelectAutocomplete/MultiSelectAutocomplete';

interface PartnerSelectorsProps {
  partners: { id: string; title: string }[];
  partnerBudgetShare?: number | null;
  fetchedPartnersData?: string[];
  handleChangeStateValues: (
    key: YouTubeTouchpointStateKeys,
    value: number | string | { id: string; title: string }
  ) => void;
}

function PartnersSelectors({
  partners,
  partnerBudgetShare,
  fetchedPartnersData,
  handleChangeStateValues,
}: PartnerSelectorsProps) {
  const { t } = useTranslation();

  const { countryForms, selectedCountry } = usePlanStore();
  const { youTubeConfig } = countryForms[selectedCountry!];
  const isPartnersError = youTubeConfig?.error && !youTubeConfig?.partners;

  return (
    <Grid container>
      <Grid item xs={3} sx={{ pr: '24px' }}>
        <FormControl fullWidth>
          <MultiSelectAutocomplete
            options={fetchedPartnersData?.map((partner) => ({
              id: partner,
              title: partner,
            }))}
            id="partnersMultiSelect"
            label={t('mediaPlanning.partners')}
            noOptionsText={t('mediaPlanning.noPartners')}
            onChange={(_: unknown, value: { id: string; title: string }) =>
              handleChangeStateValues('partners', value)
            }
            value={partners}
            limitTags={1}
            disableClearable={false}
            disabled={false}
            error={isPartnersError}
          />
          {isPartnersError && (
            <ErrorMessage message={t('mediaPlanning.errors.partners')} />
          )}
        </FormControl>
      </Grid>
      <Grid item xs={3} sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="caption" lineHeight={1}>
            {`${t(
              'mediaPlanning.partnerBudgetShare'
            )} (${convertDecimalToPercentValue(partnerBudgetShare ?? 0)}%)`}
          </Typography>
          <Slider
            valueLabelDisplay="auto"
            value={partnerBudgetShare ?? 0}
            step={0.05}
            min={touchpointsMocks.youTubeSliders.budgetShare.min}
            max={touchpointsMocks.youTubeSliders.budgetShare.max}
            marks={[
              {
                value: touchpointsMocks.youTubeSliders.budgetShare.min,
                label: convertDecimalToPercentValue(
                  touchpointsMocks.youTubeSliders.budgetShare.min
                ),
              },
              {
                value: touchpointsMocks.youTubeSliders.budgetShare.max,
                label: convertDecimalToPercentValue(
                  touchpointsMocks.youTubeSliders.budgetShare.max
                ),
              },
            ]}
            valueLabelFormat={(value) =>
              `${convertDecimalToPercentValue(value).toFixed(0)}%`
            }
            onChange={(_, newValue) =>
              handleChangeStateValues('partnerBudgetShare', +newValue)
            }
          />
        </Grid>
        <HelperTooltip tooltipData={linksConfig.partnerShareRecommendation} />
      </Grid>
    </Grid>
  );
}

export default PartnersSelectors;
